import { useEffect, useState } from "react";
import { Sparkles, Flame, Shield, BookOpen, Gamepad2, ArrowUpRight, Check, Pencil, Mail, CreditCard, Gift, Copy } from "lucide-react";
import VoicePicker from "../components/VoicePicker";
import ChangePasswordCard from "../components/ChangePasswordCard";
import PracticeReminderCard from "../components/PracticeReminderCard";
import ReadAloudTrendCard from "../components/ReadAloudTrendCard";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import Sparkline from "../components/Sparkline";
import { useAuth } from "../contexts/AuthContext";

function StatCard({ icon: Icon, label, value, tint = "#E0EDE6", color = "#2D5D4E", testId }) {
    return (
        <div className="card-rw p-6" data-testid={testId}>
            <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: tint }}>
                    <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.6} />
                </div>
                <p className="label-eyebrow">{label}</p>
            </div>
            <p className="text-3xl font-semibold text-[#1C2321]" style={{ fontFamily: "Outfit" }}>{value}</p>
        </div>
    );
}

const GAME_LABELS = {
    "letter-sound": "Letter & Sound",
    "word-build": "Word Builder",
    "rhyme": "Rhyme Match",
    "sight-words": "Sight Words Sprint",
    "unscramble": "Sentence Unscramble",
    "comprehension": "Read & Understand",
};

export default function Profile() {
    const { user, refresh } = useAuth();
    const [stats, setStats] = useState(null);
    const [timeline, setTimeline] = useState(null);
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(user?.name || "");
    const [saving, setSaving] = useState(false);
    const [emailing, setEmailing] = useState(false);
    const [optedIn, setOptedIn] = useState(true);
    const [sub, setSub] = useState(null);
    const [openingPortal, setOpeningPortal] = useState(false);
    const [referral, setReferral] = useState(null);

    const openPortal = async () => {
        setOpeningPortal(true);
        try {
            const { data } = await api.post("/payments/customer-portal", { return_url: window.location.origin + "/app/profile" });
            window.location.href = data.portal_url;
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not open the billing portal");
            setOpeningPortal(false);
        }
    };

    const saveOptIn = async (checked) => {
        setOptedIn(checked);
        try {
            await api.patch("/profile/email-prefs", { weekly_recap_opted_in: checked });
            toast.success(checked ? "Weekly recap is on" : "Weekly recap is off");
        } catch {
            toast.error("Could not save your preference");
            setOptedIn(!checked);
        }
    };

    const sendRecap = async () => {
        setEmailing(true);
        try {
            const { data } = await api.post("/email/weekly-recap");
            toast.success("Recap sent", { description: `Check ${data.sent_to} in a moment.` });
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : "Email could not be sent right now.";
            toast.error(msg);
        } finally {
            setEmailing(false);
        }
    };

    useEffect(() => {
        api.get("/profile/stats")
            .then((r) => {
                setStats(r.data);
                setName(r.data.name || "");
                if (typeof r.data.weekly_recap_opted_in === "boolean") {
                    setOptedIn(r.data.weekly_recap_opted_in);
                }
            })
            .catch(() => { toast.error("Could not load your profile stats"); setStats({ name: user?.name || "", email: user?.email || "", xp: 0, streak: 0, streak_freezes: 0, lessons_completed: 0, games_played: 0, by_game: [] }); });
        api.get("/stats/sight-words-timeline").then((r) => setTimeline(r.data)).catch(() => setTimeline({ points: [], personal_best: 0, attempts: 0, first: 0, latest: 0 }));
        api.get("/subscription/me").then((r) => setSub(r.data)).catch(() => {});
        api.get("/referral/me").then((r) => setReferral(r.data)).catch(() => {});
    }, [user?.name, user?.email]);

    const copyReferral = async () => {
        if (!referral?.share_url) return;
        try {
            await navigator.clipboard.writeText(referral.share_url);
            toast.success("Invite link copied");
        } catch {
            // Older browsers: fall back to a prompt so the user can copy manually.
            window.prompt("Copy your invite link", referral.share_url);
        }
    };

    const saveName = async () => {
        if (!name.trim() || name.trim() === stats?.name) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await api.patch("/profile", { name: name.trim() });
            toast.success("Name updated");
            setEditing(false);
            refresh();
            setStats((s) => s ? { ...s, name: name.trim() } : s);
        } catch (e) {
            toast.error("Could not update name");
        } finally {
            setSaving(false);
        }
    };

    if (!stats) return <main className="container-page py-12">Loading…</main>;

    const memberDate = stats.created_at ? new Date(stats.created_at).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : "—";
    const sw = timeline || { points: [], personal_best: 0, attempts: 0, first: 0, latest: 0 };
    const sparkVals = sw.points.map((p) => p.words_per_min);
    const growth = sw.attempts >= 2 ? sw.latest - sw.first : null;

    return (
        <main className="container-page py-12 max-w-5xl fade-up">
            <p className="label-eyebrow mb-2">Profile</p>
            <div className="flex items-start justify-between mb-2 flex-wrap gap-3">
                {editing ? (
                    <div className="flex items-center gap-2">
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            data-testid="profile-name-input"
                            className="text-4xl font-semibold text-[#1C2321] bg-transparent border-b-2 border-[#2D5D4E] focus:outline-none px-1"
                            style={{ fontFamily: "Outfit" }}
                            autoFocus
                        />
                        <button
                            onClick={saveName}
                            disabled={saving}
                            data-testid="profile-name-save"
                            className="px-3 py-2 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-sm font-medium hover:bg-[#1F4237]"
                        >
                            <Check className="w-4 h-4" />
                        </button>
                    </div>
                ) : (
                    <h1 className="text-4xl font-semibold text-[#1C2321]" style={{ fontFamily: "Outfit" }} data-testid="profile-title">
                        {stats.name}
                        <button
                            onClick={() => setEditing(true)}
                            data-testid="profile-name-edit"
                            className="ml-3 text-[#819888] hover:text-[#2D5D4E] align-middle"
                            aria-label="Edit name"
                        >
                            <Pencil className="w-5 h-5 inline" strokeWidth={1.7} />
                        </button>
                    </h1>
                )}
            </div>
            <p className="text-[#515B57] mb-10" data-testid="profile-meta">
                {stats.email} · learner since {memberDate}
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12 stagger">
                <StatCard icon={Sparkles} label="Total XP" value={stats.xp} testId="stat-total-xp" />
                <StatCard icon={Flame} label="Day streak" value={stats.streak} tint="#FDE9DD" color="#D47A55" testId="stat-total-streak" />
                <StatCard icon={Shield} label="Streak saves" value={stats.streak_freezes} tint="#E0EDE6" color="#2D5D4E" testId="stat-total-saves" />
                <StatCard icon={BookOpen} label="Lessons done" value={stats.lessons_completed} tint="#E8E2D2" color="#1F4237" testId="stat-total-lessons" />
            </div>

            {/* Referral: invite a friend, earn XP (FREE_MODE) or +30 free days (paid) */}
            {referral && (
                <section
                    className="card-rw p-6 md:p-7 mb-12"
                    style={{ borderLeft: "6px solid #D47A55" }}
                    data-testid="referral-card"
                >
                    <div className="flex flex-wrap items-start gap-5">
                        <div className="w-11 h-11 rounded-lg bg-[#FDE9DD] flex items-center justify-center flex-shrink-0">
                            <Gift className="w-5 h-5 text-[#D47A55]" strokeWidth={1.6} />
                        </div>
                        <div className="flex-1 min-w-[260px]">
                            <p className="label-eyebrow mb-1" style={{ color: "#D47A55" }}>
                                Invite a friend
                            </p>
                            <h3
                                className="text-2xl font-medium text-[#1C2321] mb-2"
                                style={{ fontFamily: "Outfit" }}
                            >
                                {sub?.free_mode
                                    ? `Share Lift Up, earn +${referral.xp_per_referral || 100} XP.`
                                    : "Share Lift Up, earn 30 free days."}
                            </h3>
                            <p className="text-[#515B57] leading-relaxed mb-4">
                                {sub?.free_mode ? (
                                    <>
                                        Send this link to someone learning to read. The moment they join, you get{" "}
                                        <span className="font-medium text-[#1C2321]">+{referral.xp_per_referral || 100} XP</span>{" "}
                                        and an <span className="font-medium text-[#1C2321]">"Invited a friend"</span> badge on your profile.
                                    </>
                                ) : (
                                    <>Send this link to someone learning to read. When they join and subscribe, we'll add a full month to your trial — no cap on friends, no cap on days.</>
                                )}
                            </p>
                            <div className="flex flex-wrap items-center gap-3">
                                <code
                                    className="px-3 py-2 rounded-lg border border-[#E5E7E2] bg-[#F5F1E4] text-[#1C2321] text-sm break-all"
                                    data-testid="referral-share-url"
                                >
                                    {referral.share_url}
                                </code>
                                <button
                                    onClick={copyReferral}
                                    data-testid="referral-copy-button"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-sm font-medium hover:bg-[#1F4237]"
                                >
                                    <Copy className="w-4 h-4" strokeWidth={1.8} /> Copy link
                                </button>
                            </div>
                            <p className="text-sm text-[#819888] mt-4">
                                Your code: <span className="font-medium text-[#1C2321]" data-testid="referral-code">{referral.code}</span>
                            </p>
                        </div>
                        <div className="text-right min-w-[120px]">
                            <p className="text-4xl font-semibold text-[#D47A55]" style={{ fontFamily: "Outfit" }} data-testid="referral-count">
                                {referral.successful_referrals}
                            </p>
                            <p className="text-sm text-[#819888]">
                                friends joined
                            </p>
                            {sub?.free_mode ? (
                                <p className="text-sm mt-2 text-[#2D5D4E] font-medium" data-testid="referral-xp-earned">
                                    +{(referral.successful_referrals || 0) * (referral.xp_per_referral || 100)} XP earned
                                </p>
                            ) : (
                                <p className="text-sm mt-2 text-[#2D5D4E] font-medium" data-testid="referral-days-earned">
                                    +{referral.days_earned} days earned
                                </p>
                            )}
                        </div>
                    </div>
                </section>
            )}

            <VoicePicker />

            <ChangePasswordCard />

            <PracticeReminderCard />

            {/* Sight Words personal-best graph */}
            <section className="card-rw p-7 mb-10" data-testid="personal-best-card">
                <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
                    <div>
                        <p className="label-eyebrow mb-1 flex items-center gap-1">
                            <ArrowUpRight className="w-3.5 h-3.5" /> Personal best
                        </p>
                        <h2 className="text-2xl font-medium text-[#1C2321]" style={{ fontFamily: "Outfit" }}>Sight Words Sprint</h2>
                        <p className="text-[#515B57]">Words you read in 60 seconds — across your last {sw.attempts || 0} {sw.attempts === 1 ? "attempt" : "attempts"}.</p>
                    </div>
                    <div className="text-right">
                        <p className="text-5xl font-semibold text-[#2D5D4E]" style={{ fontFamily: "Outfit" }} data-testid="personal-best-value">
                            {sw.personal_best}
                        </p>
                        <p className="text-sm text-[#819888]">your best · words/min</p>
                    </div>
                </div>
                <div className="overflow-x-auto pb-1">
                    <Sparkline values={sparkVals} width={Math.max(320, sparkVals.length * 30)} height={120} personalBest={sw.personal_best || null} />
                </div>
                {growth != null && growth > 0 && (
                    <p className="mt-4 text-[#2D5D4E] font-medium" data-testid="personal-best-growth">
                        You're up <span className="font-semibold">{growth} words/min</span> since you started. That's real progress.
                    </p>
                )}
                {sw.attempts === 0 && (
                    <p className="mt-4 text-[#515B57]">
                        Play the Sight Words Sprint to start your growth line. We track every attempt automatically.
                    </p>
                )}
            </section>

            <ReadAloudTrendCard />

            {/* Per-game stats */}
            <section className="mb-10">
                <p className="label-eyebrow mb-3">By game</p>
                {stats.by_game.length === 0 ? (
                    <p className="text-[#515B57]">No games played yet. Pick one from the Games page to start logging stats.</p>
                ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                        {stats.by_game.map((g) => (
                            <div key={g.game} className="card-rw p-5" data-testid={`game-stats-${g.game}`}>
                                <div className="flex items-center gap-3 mb-3">
                                    <Gamepad2 className="w-5 h-5 text-[#D47A55]" strokeWidth={1.6} />
                                    <h3 className="font-medium text-[#1C2321]" style={{ fontFamily: "Outfit" }}>
                                        {GAME_LABELS[g.game] || g.game}
                                    </h3>
                                </div>
                                <div className="grid grid-cols-3 gap-3 text-sm">
                                    <div>
                                        <p className="label-eyebrow mb-1">Plays</p>
                                        <p className="text-xl font-semibold">{g.plays}</p>
                                    </div>
                                    <div>
                                        <p className="label-eyebrow mb-1">Best</p>
                                        <p className="text-xl font-semibold">{g.best_correct}</p>
                                    </div>
                                    <div>
                                        <p className="label-eyebrow mb-1">Accuracy</p>
                                        <p className="text-xl font-semibold">{g.accuracy}%</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Subscription — hidden in FREE_MODE (free_mode=true from /subscription/me). */}
            {sub && !sub.free_mode && (
                <section className="card-rw p-7 mb-6" data-testid="subscription-card">
                    <div className="flex flex-wrap items-center gap-5">
                        <div className="w-12 h-12 rounded-lg bg-[#E0EDE6] flex items-center justify-center flex-shrink-0">
                            <CreditCard className="w-6 h-6 text-[#2D5D4E]" strokeWidth={1.6} />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <h2 className="text-xl font-medium text-[#1C2321] mb-1" style={{ fontFamily: "Outfit" }}>Subscription</h2>
                            {sub.status === "active" && (
                                <p className="text-[#515B57]">
                                    <span className="font-medium text-[#1C2321]">Active</span> · {sub.plan === "yearly" ? "Yearly ($35/year)" : "Monthly ($4/month)"}
                                </p>
                            )}
                            {sub.status === "trialing" && sub.active && (
                                <p className="text-[#515B57]">
                                    On a free trial · <span className="font-medium text-[#1C2321]">{sub.days_left} {sub.days_left === 1 ? "day" : "days"} left</span>
                                </p>
                            )}
                            {!sub.active && sub.status !== "active" && (
                                <p className="text-[#515B57]">No active subscription. Your progress is saved — subscribe anytime.</p>
                            )}
                        </div>
                        {sub.status === "active" ? (
                            <button
                                onClick={openPortal}
                                disabled={openingPortal}
                                data-testid="manage-subscription-button"
                                className="px-5 py-2.5 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] font-medium hover:bg-[#E8E2D2] disabled:opacity-60"
                            >
                                {openingPortal ? "Opening…" : "Manage subscription"}
                            </button>
                        ) : (
                            <Link
                                to="/pricing"
                                data-testid="profile-see-plans"
                                className="px-5 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]"
                            >
                                See plans
                            </Link>
                        )}
                    </div>
                </section>
            )}

            {/* Free-mode acknowledgement — replaces the subscription card. */}
            {sub?.free_mode && (
                <section className="card-rw p-7 mb-6" data-testid="free-membership-card">
                    <div className="flex items-center gap-5">
                        <div className="w-12 h-12 rounded-lg bg-[#E0EDE6] flex items-center justify-center flex-shrink-0">
                            <CreditCard className="w-6 h-6 text-[#2D5D4E]" strokeWidth={1.6} />
                        </div>
                        <div>
                            <h2 className="text-xl font-medium text-[#1C2321] mb-1" style={{ fontFamily: "Outfit" }}>Free membership</h2>
                            <p className="text-[#515B57]">
                                Every lesson, game, and tutor session is included — no card, no subscription to manage.
                            </p>
                        </div>
                    </div>
                </section>
            )}

            {/* Email recap */}
            <section className="card-rw p-7 mb-6" data-testid="email-recap-card">
                <div className="flex flex-wrap items-center gap-5 mb-5">
                    <div className="w-12 h-12 rounded-lg bg-[#E0EDE6] flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-[#2D5D4E]" strokeWidth={1.6} />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                        <h2 className="text-xl font-medium text-[#1C2321] mb-1" style={{ fontFamily: "Outfit" }}>Weekly progress email</h2>
                        <p className="text-[#515B57]">
                            A short, plainspoken recap of your week sent to <span className="font-medium text-[#1C2321]">{stats.email}</span>.
                            Delivered automatically every Sunday evening.
                        </p>
                    </div>
                    <button
                        onClick={sendRecap}
                        disabled={emailing}
                        data-testid="email-recap-button"
                        className="px-5 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237] disabled:opacity-60"
                    >
                        {emailing ? "Sending…" : "Send me one now"}
                    </button>
                </div>
                <label className="flex items-center gap-3 cursor-pointer select-none pt-4 border-t border-[#E5E7E2]" data-testid="email-optin-toggle">
                    <input
                        type="checkbox"
                        checked={optedIn}
                        onChange={(e) => saveOptIn(e.target.checked)}
                        className="w-5 h-5 accent-[#2D5D4E]"
                        data-testid="email-optin-checkbox"
                    />
                    <span className="text-[#1C2321]">
                        Send me the weekly recap automatically each Sunday
                    </span>
                </label>
            </section>
        </main>
    );
}
